import { useEffect } from 'react';

/**
 * Wrapper sécurisé pour le tracking Umami qui ne plante pas si Umami est bloqué
 */
export const trackEvent = (eventName: string, data?: Record<string, any>): void => {
  try {
    if (typeof window !== 'undefined' && window.umami) {
      window.umami.track(eventName, data);
    }
    // Log en développement pour débogage
    if (import.meta.env.DEV) {
      console.log('[Umami Track]', eventName, data);
    }
  } catch (error) {
    console.error('Umami tracking error:', error);
  }
};

/**
 * Identifie l'utilisateur courant auprès d'Umami (remplit session.distinct_id).
 * Permet ensuite de croiser les sessions Umami avec les users Postgres applicatifs.
 * Identifie aussi Microsoft Clarity pour pouvoir filtrer les sessions par userId.
 */
export const identifyUser = (userId: number, props?: Record<string, any>): void => {
  try {
    if (typeof window !== 'undefined' && window.umami?.identify) {
      window.umami.identify({ userId, ...props });
    }
    const w = window as unknown as { clarity?: (...args: unknown[]) => void };
    if (typeof window !== 'undefined' && typeof w.clarity === 'function') {
      w.clarity('identify', String(userId));
      w.clarity('set', 'userId', String(userId));
      if (props?.provider) w.clarity('set', 'provider', String(props.provider));
      if (props?.role) w.clarity('set', 'role', String(props.role));
    }
    if (import.meta.env.DEV) {
      console.log('[Identify]', userId, props);
    }
  } catch (error) {
    console.error('Identify error:', error);
  }
};

/**
 * Réinitialise l'identité Umami (au logout).
 */
export const resetUmamiIdentity = (): void => {
  try {
    if (typeof window !== 'undefined' && window.umami?.reset) {
      window.umami.reset();
    }
  } catch (error) {
    console.error('Umami reset error:', error);
  }
};

/**
 * Hook pour tracker les vues de page
 */
export const usePageViewTracking = (pageName: string): void => {
  useEffect(() => {
    trackEvent('page_view', { page: pageName });
  }, [pageName]);
};

/**
 * Hook pour tracker les événements avec dépendances
 */
export const useEventTracking = (eventName: string, dependencies: any[] = []): ((data?: Record<string, any>) => void) => {
   
  const memoizedEventName = eventName;
  
  return (data?: Record<string, any>) => {
    trackEvent(memoizedEventName, data);
  };
};